import { useState } from 'react';
import { Checkbox } from '../ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { categorizeMetrics, getRequirementLabels } from '../../utils/metricsUtils';
import questionCircle from '../../assets/icons/question_circle.svg';
import downIcon from '../../assets/icons/down_icon.svg';
import rightIcon from '../../assets/icons/right_icon.svg';

const MetricTooltip = ({ metric }) => {  
  const requirementLabels = getRequirementLabels(metric);
  const hasDescription = Boolean(metric?.description);

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="p-0 m-0 bg-transparent border-none"
          >
            <img src={questionCircle} alt="Info" className="w-4 h-4 opacity-50" />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="max-w-[220px] px-2 py-1 space-y-1 border border-[#E2E8F0] rounded-xs shadow-[0px_1px_3px_0px_#0000001A]"
        >
          {hasDescription ? (
            <p className="text-[10px] leading-[14px] text-[#020817]">{metric.description}</p>
          ) : null}
          {requirementLabels.length > 0 && (
            <div className="flex flex-wrap gap-[2px]">
              {requirementLabels.map((label) => (
                <span
                  key={`${metric?.key || metric?.id || 'metric'}-${label}`}
                  className="px-1 py-[2px] text-[10px] leading-[14px] text-[#2269DC] bg-[#D8E6FD] rounded-sm"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const MetricsList = ({
  metricList,
  onMetricToggle,
  error,
  onExpandedCategoriesChange
}) => {
  const [expandedCategories, setExpandedCategories] = useState({
    standard: true,
    bertscore: false,
    comet: false,
    metricx: false
  });

  // Notify parent when expanded categories change
  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const newState = {
        ...prev,
        [category]: !prev[category]
      };
      if (onExpandedCategoriesChange) {
        onExpandedCategoriesChange(newState);
      }
      return newState;
    });
  };

  const categories = categorizeMetrics(metricList || []);

  // Calculate checkbox states for categories
  const getCategoryState = (categoryMetrics) => {
    if (categoryMetrics.length === 0) return false;
    const selectedCount = categoryMetrics.filter(m => m && m.selected).length;
    if (selectedCount === 0) return false;
    if (selectedCount === categoryMetrics.length) return true;
    return 'indeterminate';
  };

  // Calculate "All Metrics" state
  const allMetrics = metricList || [];
  const allMetricsCount = allMetrics.length;
  const allSelectedCount = allMetrics.filter(m => m && m.selected).length;
  const allMetricsState = allMetricsCount === 0 
    ? false 
    : allSelectedCount === allMetricsCount 
      ? true 
      : allSelectedCount > 0 
        ? 'indeterminate' 
        : false;

  const handleMetricToggle = (metricKey) => {
    onMetricToggle(metricKey);
  };

  const handleCategoryToggle = (categoryMetrics) => {
    const selectedCount = categoryMetrics.filter(m => m && m.selected).length;
    const shouldSelect = selectedCount < categoryMetrics.length;
    
    categoryMetrics.forEach(metric => {
      if (metric && metric.key && metric.selected !== shouldSelect) {
        onMetricToggle(metric.key);
      }
    });
  };

  const handleAllMetricsToggle = () => {
    const shouldSelect = allSelectedCount < allMetricsCount;
    allMetrics.forEach(metric => {
      if (metric && metric.key && metric.selected !== shouldSelect) {
        onMetricToggle(metric.key);
      }
    });
  };

  // toggleCategory moved above

  if (error) {
    return (
      <div className="text-sm text-red-500 text-center py-4">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full">
      <div 
        className="px-2 py-3 border-b border-[#E2E8F0] hover:bg-[#F9FAFB] cursor-pointer"
        onClick={handleAllMetricsToggle}
      >
        <div className="flex items-center space-x-3">
          <Checkbox
            checked={allMetricsState}
            onCheckedChange={handleAllMetricsToggle}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="flex-1">
            <span className="text-base font-medium text-[#020817]">All Metrics</span>
          </div>
        </div>
      </div>

      <div>
        {/* Standard Category */}
        {categories.standard.length > 0 && (
          <>
            <div className="my-2">
              <div 
                className="px-2 py-1 hover:bg-[#F9FAFB] cursor-default"
                onClick={() => toggleCategory('standard')}
              >
                <div className="flex items-center space-x-2">
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategory('standard');
                    }}
                  >
                    <img 
                      src={expandedCategories.standard ? downIcon : rightIcon} 
                      alt={expandedCategories.standard ? "Collapse" : "Expand"}
                      className="w-4 h-4"
                    />
                  </div>
                  <Checkbox
                    checked={getCategoryState(categories.standard)}
                    onCheckedChange={() => handleCategoryToggle(categories.standard)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1">
                    <span className="text-base font-medium text-[#020817]">
                      Standard <span className="text-[#9CA3AF]">({categories.standard.length})</span>
                    </span>
                  </div>
                </div>
              </div>
              {expandedCategories.standard && categories.standard.map((metric) => (
                <div
                  key={metric.key} 
                  className="pl-[60px] pr-3 py-1 hover:bg-[#F9FAFB] cursor-pointer"
                  onClick={() => handleMetricToggle(metric.key)}
                >
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={Boolean(metric.selected)}
                      disabled={!Boolean(metric.enabled)}
                      onCheckedChange={() => handleMetricToggle(metric.key)}
                      onClick={(e) => e.stopPropagation()}
                    />
                      <div className="flex items-center gap-2 flex-1 h-6">
                        <span className="text-sm font-medium text-[#020817]">
                          {metric.name || 'Unknown Metric'}
                        </span>
                        <MetricTooltip metric={metric} />
                      </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* BERTScore */}
        {categories.bertscore.length > 0 && (
          <>
            <div className="border-b border-[#E2E8F0] my-2" />
            <div>
              <div 
                className="px-2 py-1 hover:bg-[#F9FAFB] cursor-default"
                onClick={() => toggleCategory('bertscore')}
              >
                <div className="flex items-center space-x-2">
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategory('bertscore');
                    }}
                  >
                    <img 
                      src={expandedCategories.bertscore ? downIcon : rightIcon} 
                      alt={expandedCategories.bertscore ? "Collapse" : "Expand"}
                      className="w-4 h-4"
                    />
                  </div>
                  <Checkbox
                    checked={getCategoryState(categories.bertscore)}
                    onCheckedChange={() => handleCategoryToggle(categories.bertscore)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1">
                    <span className="text-base font-medium text-[#020817]">
                      BERTScore family <span className="text-[#9CA3AF]">({categories.bertscore.length})</span>
                    </span>
                  </div>
                </div>
              </div>
              {expandedCategories.bertscore && categories.bertscore.map((metric) => (
                <div 
                  key={metric.key} 
                  className="pl-[60px] pr-3 py-1 hover:bg-[#F9FAFB] cursor-pointer"
                  onClick={() => handleMetricToggle(metric.key)}
                >
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={Boolean(metric.selected)}
                      disabled={!Boolean(metric.enabled)}
                      onCheckedChange={() => handleMetricToggle(metric.key)}
                      onClick={(e) => e.stopPropagation()}
                    />
                      <div className="flex items-center gap-2 flex-1 h-6">
                        <span className="text-sm font-medium text-[#020817]">
                          {metric.name || 'Unknown Metric'}
                        </span>
                        <MetricTooltip metric={metric} />
                      </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* COMET */}
        {categories.comet.length > 0 && (
          <>
            <div className="border-b border-[#E2E8F0] my-2" />
            <div>
              <div 
                className="px-2 py-1 hover:bg-[#F9FAFB] cursor-default"
                onClick={() => toggleCategory('comet')}
              >
                <div className="flex items-center space-x-2">
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategory('comet');
                    }}
                  >
                    <img 
                      src={expandedCategories.comet ? downIcon : rightIcon} 
                      alt={expandedCategories.comet ? "Collapse" : "Expand"}
                      className="w-4 h-4"
                    />
                  </div>
                  <Checkbox
                    checked={getCategoryState(categories.comet)}
                    onCheckedChange={() => handleCategoryToggle(categories.comet)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1">
                    <span className="text-base font-medium text-[#020817]">
                      COMET family <span className="text-[#9CA3AF]">({categories.comet.length})</span>
                    </span>
                  </div>
                </div>
              </div>
              {expandedCategories.comet && categories.comet.map((metric) => (
                <div 
                  key={metric.key} 
                  className="pl-[60px] pr-3 py-1 hover:bg-[#F9FAFB] cursor-pointer"
                  onClick={() => handleMetricToggle(metric.key)}
                >
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={Boolean(metric.selected)}
                      disabled={!Boolean(metric.enabled)}
                      onCheckedChange={() => handleMetricToggle(metric.key)}
                      onClick={(e) => e.stopPropagation()}
                    />
                      <div className="flex items-center gap-2 flex-1 h-6">
                        <span className="text-sm font-medium text-[#020817]">
                          {metric.name || 'Unknown Metric'}
                        </span>
                        <MetricTooltip metric={metric} />
                      </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Google / MetricX */}
        {categories.metricx.length > 0 && (
          <>
            <div className="border-b border-[#E2E8F0] my-2" />
            <div>
              <div 
                className="px-2 py-1 hover:bg-[#F9FAFB] cursor-default"
                onClick={() => toggleCategory('metricx')}
              >
                <div className="flex items-center space-x-2">
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategory('metricx');
                    }}
                  >
                    <img 
                      src={expandedCategories.metricx ? downIcon : rightIcon} 
                      alt={expandedCategories.metricx ? "Collapse" : "Expand"}
                      className="w-4 h-4"
                    />
                  </div>
                  <Checkbox
                    checked={getCategoryState(categories.metricx)}
                    onCheckedChange={() => handleCategoryToggle(categories.metricx)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1">
                    <span className="text-base font-medium text-[#020817]">
                      Google / MetricX family <span className="text-[#9CA3AF]">({categories.metricx.length})</span>
                    </span>
                  </div>
                </div>
              </div>
              {expandedCategories.metricx && categories.metricx.map((metric) => (
                <div 
                  key={metric.key} 
                  className="pl-[60px] pr-3 py-1 hover:bg-[#F9FAFB] cursor-pointer"
                  onClick={() => handleMetricToggle(metric.key)}
                >
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={Boolean(metric.selected)}
                      disabled={!Boolean(metric.enabled)}
                      onCheckedChange={() => handleMetricToggle(metric.key)}
                      onClick={(e) => e.stopPropagation()}  
                    />
                      <div className="flex items-center gap-2 flex-1 h-6">
                        <span className="text-sm font-medium text-[#020817]">
                          {metric.name || 'Unknown Metric'}
                        </span>
                        <MetricTooltip metric={metric} />
                      </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MetricsList;
